import RestAPIUtils from './rest-api-utils';

function isLocalhost() {
    const hostname = window?.location?.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
}

class PartnerApi {
    static PARTNER_API_CONFIG = {
        apiUrl: process.env.REACT_APP_PARTNER_API_URL,
    };

    static _clientId = process.env.REACT_APP_PARTNER_CLIENT_ID;
    static _clientSecret = process.env.REACT_APP_PARTNER_CLIENT_SECRET;

    static _storageKey = 'partnerApiToken';
    static _accessToken = null;

    static _getStoredToken() {
        try {
            const data = JSON.parse(localStorage.getItem(PartnerApi._storageKey));
            if (!data || !data.accessToken || !data.expiresAt) {
                console.warn('[PartnerApi] No valid token found in localStorage.');
                return null;
            }
            console.warn('[PartnerApi] Loaded stored token from localStorage.');
            return data;
        } catch (err) {
            console.error('[PartnerApi] Failed to parse stored token:', err);
            return null;
        }
    }

    static _storeToken(accessToken, expiresInSeconds) {
        const expiresAt = Date.now() + expiresInSeconds * 1000 - 5000; // buffer
        const data = { accessToken, expiresAt };
        localStorage.setItem(PartnerApi._storageKey, JSON.stringify(data));
        console.warn('[PartnerApi] Stored new access token with expiry in seconds:', expiresInSeconds);
    }

    static async _ensureValidToken(token = null) {
        console.warn('[PartnerApi:_ensureValidToken] token: ', token);

        const urlToken = new URLSearchParams(window.location.search).get('token');
        console.warn('[PartnerApi:_ensureValidToken] urlToken: ', urlToken);
        if (urlToken) {
            PartnerApi._accessToken = urlToken;
            return;
        }

        if (token) {
            PartnerApi._storeToken(token, 3600);
            PartnerApi._accessToken = token;
            return;
        }

        const stored = PartnerApi._getStoredToken();
        const now = Date.now();

        if (stored && stored.accessToken && now < stored.expiresAt) {
            console.warn('[PartnerApi] Reusing valid stored token.');
            PartnerApi._accessToken = stored.accessToken;
            return;
        }

        if (!isLocalhost()) {
            throw new Error(
                '[PartnerApi] Refusing to generate OAuth token outside localhost. ' +
                    'Token must be passed explicitly or via URL.',
            );
        }

        if (!PartnerApi._clientId || !PartnerApi._clientSecret) {
            throw new Error('[PartnerApi] Cannot fetch OAuth token — client ID or secret missing in dev.');
        }

        console.warn('[PartnerApi] Fetching new OAuth token (localhost fallback)...');
        const apiPath = `/v1/oauth/token`;
        const payload = {
            client_id: PartnerApi._clientId,
            client_secret: PartnerApi._clientSecret,
            external_user_id: 'external-user-4',
        };

        try {
            const response = await RestAPIUtils.makePartnerRequest(
                null,
                PartnerApi.PARTNER_API_CONFIG,
                apiPath,
                'POST',
                payload,
            );

            PartnerApi._accessToken = response.access_token;
            PartnerApi._storeToken(response.access_token, response.expires_in);
            console.warn('[PartnerApi] Successfully fetched new OAuth token.');
        } catch (error) {
            console.error('[PartnerApi] Failed to fetch OAuth token:', error);
            throw error;
        }
    }

    static async _makeAuthenticatedGetRequest(token = null, apiPath, queryParams = {}) {
        await PartnerApi._ensureValidToken();
        return RestAPIUtils.makePartnerRequest(
            PartnerApi._accessToken,
            PartnerApi.PARTNER_API_CONFIG,
            apiPath,
            'GET',
            queryParams,
        );
    }

    static async getHandoutContent(token = null, ids) {
        console.warn('[getHandoutContent]', token);
        console.warn('[getHandoutContent] process.env.NODE_ENV:', process.env.NODE_ENV);
        const apiPath = `/v1/handouts/content`;
        const params = { ids, lang: 'en-UK' };

        try {
            if (token) {
                // Use the token in external UI loading Cetra as iFrame with query param
                console.warn('[PartnerApi] Using externally provided token.', token);
                PartnerApi._accessToken = token;
                return await RestAPIUtils.makePartnerRequest(
                    token,
                    PartnerApi.PARTNER_API_CONFIG,
                    apiPath,
                    'GET',
                    params,
                );
            } else {
                console.warn('[PartnerApi] No token provided, using internal OAuth flow.');
                return await PartnerApi._makeAuthenticatedGetRequest(token, apiPath, params);
            }
        } catch (err) {
            console.error(`[PartnerApi] Failed to fetch handout content for ids=${ids}:`, err);
            throw err;
        }
    }

    static async generatePresignedUrls(token = null, files, contentCategory) {
        console.warn('[PartnerApi:generatePresignedUrls] token:', token, ' accessToken:', PartnerApi._accessToken);
        const apiPath = '/v1/handouts/generate-presigned-urls';
        const payload = {
            contentCategory: contentCategory, // 'image' | 'content' | 'clinic_image' | 'whiteboard' if needed
            files,
        };

        console.warn('[PartnerApi:generatePresignedUrls] payload:', JSON.stringify(payload, null, 2));
        console.warn('[PartnerApi:generatePresignedUrls] process.env.NODE_ENV:', process.env.NODE_ENV);
        try {
            if (token) {
                console.warn('[PartnerApi:generatePresignedUrls with token]');
                return await RestAPIUtils.makePartnerRequest(
                    token,
                    PartnerApi.PARTNER_API_CONFIG,
                    apiPath,
                    'POST',
                    payload,
                );
            } else {
                console.warn('[PartnerApi:generatePresignedUrls no token]');
                await PartnerApi._ensureValidToken();
                return await RestAPIUtils.makePartnerRequest(
                    PartnerApi._accessToken,
                    PartnerApi.PARTNER_API_CONFIG,
                    apiPath,
                    'POST',
                    payload,
                );
            }
        } catch (error) {
            console.error('[PartnerApi] Failed to generate presigned URLs:', error);
            throw error;
        }
    }

    static async getUserAndLogo(token = null) {
        const fetchWithToken = async (path) => {
            if (!token) {
                await PartnerApi._ensureValidToken();
                token = PartnerApi._accessToken;
            }
            return RestAPIUtils.makePartnerRequest(token, PartnerApi.PARTNER_API_CONFIG, path, 'GET');
        };

        try {
            const [userDetails, logoData] = await Promise.all([
                fetchWithToken('/v1/partner-user'),
                fetchWithToken('/v1/partner-user/image'),
            ]);

            const clinic = userDetails.clinicData || {};
            const url = logoData?.url;

            return {
                clinicData: {
                    clinicName: clinic.clinicName || '',
                    clinicNumber: clinic.clinicNumber || '',
                    emailAddress: clinic.emailAddress || '',
                    url: url || '',
                    accountType: 'Clinic',
                    showLogo: !!url,
                },
            };
        } catch (err) {
            console.error('[PartnerApi] Failed to fetch user and/or logo data:', err);
            throw err;
        }
    }
}

if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.warn('[PartnerApi] Running in development mode – exposing dev-only helpers.');
}

if (process.env.NODE_ENV === 'production') {
    // Keep _ensureValidToken for staging and prod
    for (const key of ['_clientId', '_clientSecret']) {
        delete PartnerApi[key];
    }
}

export default PartnerApi;
